/**
 * The local copy of which chats are muted, and until when.
 *
 * `muteStatus` from the server says a chat was muted but not when that ends, so a
 * mute with a duration has to be re-checked against the clock. This module owns the
 * `tempMuteUser` store every part of the UI reads that answer from.
 */

import SDK from '../../Components/SDK';
import {
    encryptAndStoreInLocalStorage,
    getFromLocalStorageAndDecrypt,
} from '../../Components/WebChat/WebChatEncryptDecrypt';

const STORE_KEY = 'tempMuteUser';

const readStore = () => {
    const stored = getFromLocalStorageAndDecrypt(STORE_KEY);
    if (!stored) return {};
    try {
        return JSON.parse(stored);
    } catch (e) {
        return {};
    }
};

const writeStore = rows => encryptAndStoreInLocalStorage(STORE_KEY, JSON.stringify(rows));

/**
 * Records that a chat is muted, and until when.
 *
 * @param {string} name Chat id.
 * @param {boolean} stateData Whether the chat is muted.
 * @param {number} mutedUntil Epoch millis the mute ends at; null for no time limit.
 */
export const setTempMute = (name, stateData, mutedUntil = null) => {
    writeStore({ ...readStore(), [name]: { fromUserId: name, isMuted: stateData, mutedUntil } });
};

/**
 * Drops a chat from the local copy.
 *
 * @param {string} name Chat id.
 */
export const removeTempMute = name => {
    const rows = readStore();
    delete rows[name];
    writeStore(rows);
};

/**
 * Reports whether a chat is muted right now.
 *
 * @param {string} chatId Chat id.
 * @returns {boolean} True while the mute is still in effect.
 */
export const isChatMuted = chatId => {
    const row = readStore()[chatId];
    if (!row?.isMuted) return false;

    return !row.mutedUntil || row.mutedUntil > Date.now();
};

/**
 * Lists the chat ids whose mute has elapsed.
 *
 * @returns {Array} Chat ids to drop from the local copy.
 */
export const getExpiredMutes = () => {
    const rows = readStore();
    return Object.keys(rows).filter(id => rows[id]?.mutedUntil && rows[id].mutedUntil <= Date.now());
};

/**
 * Replaces the local copy with a muted-chat response.
 *
 * The server leaves out chats whose duration has elapsed, so whatever is missing from
 * the response is unmuted here.
 *
 * @param {object} data Payload of GET /users/muted.
 */
export const applyMutedChats = (data = {}) => {
    const { mutedChats = [] } = data;
    writeStore(
        mutedChats.reduce((acc, { chatId, chatType, mutedUntil = null }) => {
            acc[chatId] = { fromUserId: chatId, chatType, isMuted: true, mutedUntil };
            return acc;
        }, {}),
    );
};

/**
 * Fetches the muted chats and replaces the local copy with them.
 *
 * The mute stanza carries no end time, so a device that did not perform the mute has
 * to ask for it.
 *
 * @returns {Promise<boolean>} True when the copy was refreshed.
 */
export const syncMutedChats = async () => {
    const response = await SDK.getMutedUsers();
    if (response?.statusCode !== 200) return false;

    applyMutedChats(response.data);
    return true;
};
